import { faTrashCan } from "@fortawesome/pro-duotone-svg-icons/faTrashCan";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, Form, Input, Select, Table, Typography } from "antd";
import { useEffect, useState } from "react";
import OrgService from "../services/Org/OrgService";
import PurchaseOrderService from "../services/PurchaseOrder/PurchaseOrderService";

const { Title } = Typography;
const { Option } = Select;

export default function PurchaseOrderPage() {
  const [supplier, setSupplier] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clientOrgReference, setClientOrgReference] = useState("");
  const [notes, setNotes] = useState("");
  const [containerNumber, setContainerNumber] = useState("");
  const [priorityID, setPriorityID] = useState(3);
  const [orderNum, setOrderNum] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    getSupplier();
  }, []);

  const getSupplier = async () => {
    try {
      const response = await OrgService.getSupplierDropDown(null);
      setSuppliers(response?.data || []);
    } catch {
      setSuppliers([]);
    }
  };

  const addItem = () => {
    setOrderItems([
      ...orderItems,
      { key: Date.now(), sku: "", description: "", quantity: 1, errors: {} },
    ]);
  };

  const deleteItem = (key) => {
    setOrderItems(orderItems.filter((item) => item.key !== key));
  };

  const handleItemChange = (key, field, value) => {
    setOrderItems(
      orderItems.map((item) =>
        item.key === key
          ? { ...item, [field]: value, errors: { ...item.errors, [field]: "" } }
          : item
      )
    );
  };

  const validateOrderItems = () => {
    let isValid = true;
    const updatedItems = orderItems.map((item) => {
      const errors = {};
      if (!item.sku.trim()) {
        errors.sku = "SKU is required";
        isValid = false;
      }
      if (!item.description.trim()) {
        errors.description = "Description is required";
        isValid = false;
      }
      if (!item.quantity || item.quantity < 1) {
        errors.quantity = "Quantity must be at least 1";
        isValid = false;
      }
      return { ...item, errors };
    });
    setOrderItems(updatedItems);
    return isValid;
  };

  const createOrder = () => {
    form.validateFields().then(() => {
      if (!supplier || !clientOrgReference.trim() || !validateOrderItems()) return;
      const payload = {
        clientOrgReference,
        supplierID: supplier,
        notes,
        containerNumber,
        priorityID,
        lineItemRequest: orderItems.map((item) => ({
          sku: item.sku,
          description: item.description,
          qtyOrdered: item.quantity,
          qtyReceived: 0,
        })),
      };
      PurchaseOrderService.createPurchaseOrder(payload)
        .then((response) => setOrderNum(response.data.id))
        .catch(() => { });
    });
  };

  const priorityOptions = [
    { value: 1, label: "Very High" },
    { value: 2, label: "High" },
    { value: 3, label: "Medium" },
    { value: 4, label: "Low" },
    { value: 5, label: "Lowest" },
  ];

  const columns = [
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      render: (_, record) => (
        <div>
          <Input
            placeholder="Enter SKU"
            className="w-full"
            value={record.sku}
            onChange={(e) =>
              handleItemChange(record.key, "sku", e.target.value)
            }
          />
          {record.errors?.sku && (
            <span className="text-red-500 text-sm">{record.errors.sku}</span>
          )}
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (_, record) => (
        <div>
          <Input
            placeholder="Enter Description"
            className="w-full"
            value={record.description}
            onChange={(e) =>
              handleItemChange(record.key, "description", e.target.value)
            }
          />
          {record.errors?.description && (
            <span className="text-red-500 text-sm">
              {record.errors.description}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (_, record) => (
        <div>
          <Input
            placeholder="Enter Quantity"
            className="w-full"
            type="number"
            min={1}
            value={record.quantity}
            onChange={(e) =>
              handleItemChange(record.key, "quantity", e.target.value)
            }
          />
          {record.errors?.quantity && (
            <span className="text-red-500 text-sm">
              {record.errors.quantity}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button onClick={() => deleteItem(record.key)}>
          <FontAwesomeIcon icon={faTrashCan} className="text-red-500" />
        </Button>
      ),
    },
  ];

  if (orderNum) {
    return (
      <div className="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded text-center">
        Order {orderNum} created successfully!
      </div>
    );
  }

  return (
    <Form form={form} className="w-full p-8 bg-white shadow-lg rounded-lg">
      <Card className="mb-8 shadow-md p-6 min-h-[200px]">
        <Title level={4} className="text-center mb-4">
          Supplier Information
        </Title>
        <Form.Item label="Select Supplier" required>
          <Select
            placeholder="Choose Supplier"
            className="w-full h-12"
            onChange={setSupplier}
            value={supplier}
          >
            {suppliers.map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Card>

      <Card className="mb-8 shadow-md p-6 min-h-[200px]">
        <Title level={4} className="mb-4">
          Additional Details
        </Title>
        <div className="grid grid-cols-2 gap-6">
          <Form.Item label="PO Reference" className="w-full" required>
            <Input
              className="h-12"
              placeholder="PO Reference"
              value={clientOrgReference}
              onChange={(e) => setClientOrgReference(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Container Number" className="w-full">
            <Input
              className="h-12"
              placeholder="Container No."
              value={containerNumber}
              onChange={(e) => setContainerNumber(e.target.value)}
            />
          </Form.Item>
        </div>
        <Form.Item label="Notes">
          <Input.TextArea
            className="h-24"
            placeholder="Any additional notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Form.Item>
        {/* <Form.Item label="Priority">
          <Select
            className="w-1/2 h-12"
            value={priorityID}
            onChange={setPriorityID}
          >
            {priorityOptions.map((p) => (
              <Option key={p.value} value={p.value}>
                {p.label}
              </Option>
            ))}
          </Select>
        </Form.Item> */}
        <Form.Item label="Priority">
          <Select
            className="w-full h-8 text-sm"
            value={priorityID}
            onChange={setPriorityID}
          >
            {priorityOptions.map((p) => (
              <Option key={p.value} value={p.value}>
                {p.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Card>

      <Card className="mb-8 shadow-md p-6 min-h-[200px]">
        <Title level={4} className="mb-4">
          Order Items
        </Title>
        <div>
          <Table
            columns={columns}
            dataSource={orderItems}
            pagination={false}
            className="mb-4"
          />
        </div>
        <div className="flex justify-center items-center">
          <Button
            type="primary"
            onClick={addItem}
          >
            Add Item
          </Button>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button
          type="primary"
          onClick={createOrder}
          className="py-3 px-8 h-12 text-lg"
        >
          Create Order
        </Button>
      </div>
    </Form>
  );
}
